import React, { FC, useEffect, useState } from "react";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  TextField,
  Chip,
  Typography,
  Tooltip,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Close, ContentPaste, Refresh, Visibility, VisibilityOff } from "@mui/icons-material";
import MButton from "components/momentum/MButton";
import { getDirectUrl, parseWebVtt } from "utils/index";
import ReactMarkdown from "react-markdown";
// import { toast } from "react-toastify";

const useStyles = makeStyles(() => ({
  titleContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 24px",
    borderBottom: "1px solid #DEDEDE",
  },
  title: {
    fontSize: 16,
    fontWeight: 500,
    color: "#171B1F",
    lineHeight: "24px",
  },
  content: {
    padding: "16px 24px",
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: "#535759",
    marginBottom: 4,
  },
  clipboardSection: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  clipboardTitle: {
    fontSize: 12,
    fontWeight: 500,
    color: "#535759",
  },
  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  chip: {
    cursor: "pointer",
    backgroundColor: "#E6FBFF",
    border: "1px solid #00a0d1",
    color: "#007AA3",
    fontSize: 12,
    fontWeight: 500,
    height: 28,
    "&:hover": {
      backgroundColor: "#c7e5ea !important",
    },
  },
  previewLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: "#535759",
    marginBottom: 4,
  },
  actions: {
    padding: "12px 24px",
    borderTop: "1px solid #DEDEDE",
    gap: 8,
    justifyContent: "flex-end",
  },
}));

interface ParsedRecording {
  title: string;
  link: string;
  password: string;
}

const parseClipboard = (text: string): ParsedRecording[] => {
  const results: ParsedRecording[] = [];
  // Split by lines and look for blocks
  const lines = text.split(/\r?\n/).map((l) => l.trim());
  let i = 0;
  while (i < lines.length) {
    const titleMatch = lines[i].match(/^Webex meeting recording:\s*(.+)$/i);
    if (titleMatch) {
      const title = titleMatch[1].trim();
      let link = "";
      let password = "";
      // Look ahead for Recording link and Password within the next few lines
      for (let j = i + 1; j < Math.min(i + 5, lines.length); j++) {
        const linkMatch = lines[j].match(/^Recording link:\s*(.+)$/i);
        const passwordMatch = lines[j].match(/^Password:\s*(.+)$/i);
        if (linkMatch) link = linkMatch[1].trim();
        if (passwordMatch) password = passwordMatch[1].trim();
      }
      if (link) {
        results.push({ title, link, password });
      }
    }
    i++;
  }
  return results;
};

const getRecordingStreamInfo = async (link: string, password: string): Promise<string | any> => {
  if (!link) return "";
  let endpoint = link;
  if (link.includes("RCID")) {
    try {
      endpoint = await getDirectUrl(link);
      //console.log("Resolved Webex URL:", endpoint);
    } catch (error) {
      console.error("failed to redirect link", error);
      throw error;
    }
  }
  if (endpoint && endpoint.includes("recording/playback")) {
    const url = new URL(endpoint);
    const domain = url.origin;
    const recordingId = url.pathname.split("/").filter(Boolean).at(-1);

    if (domain && recordingId) {
      const recordingApi = `${domain}/webappng/api/v1/recordings/${recordingId}/stream`;
      try {
        const response = await fetch(
          `/api/webex/recording/getRecordingStream?recordingApi=${encodeURIComponent(recordingApi)}${password ? `&password=${encodeURIComponent(password)}` : ""}`
        );
        if (response.ok) {
          const data = await response.json();

          if (!data || !data.recordName) {
            throw new Error("Failed to fetch recording stream info.");
          }

          return {
            recordName: data?.recordName ?? "",
            playUrl: data?.playUrl ?? null,
            suggestedNoteUrl: data?.suggestedNoteUrl ?? null,
            transcriptURL: data?.downloadRecordingInfo?.downloadInfo?.transcriptURL ?? null,
            actionItemUrl: data?.actionItemUrl ?? null,
          };
        } else {
          console.error("getRecordingStream API error:", response.status, await response.text());
          throw new Error(`Upstream request failed with status ${response.status}`);
        }
      } catch (error) {
        console.error("failed to fetch recording stream", error);
        throw error;
        return {
          recordName: null,
          playUrl: null,
          suggestedNoteUrl: null,
          transcriptURL: null,
          actionItemUrl: null,
        };
      }
    }
  }
};

interface RecordingStreamInfo {
  recordName: string | null;
  playUrl: string | null;
  suggestedNoteUrl: string | null;
  transcriptURL: string | null;
  actionItemUrl: string | null;
}

interface RecordingContents {
  recordingName: string | null;
  /** Raw text of the AI-generated meeting notes */
  suggestedNote: string | null;
  /** Raw text of the meeting transcript */
  transcript: string | null;
  /** Raw text of the action items */
  actionItems: string | null;
  /** Playback URL (not fetched – included as-is) */
  playUrl: string | null;
}

/**
 * Fetches the text content of each resource URL returned by the recording
 * stream API in parallel. Network failures for individual URLs are silently
 * swallowed so that one bad URL never blocks the others.
 */
const fetchRecordingContents = async (urls: RecordingStreamInfo): Promise<RecordingContents> => {
  const fetchText: any = async (url: string | null): Promise<string | null> => {
    if (!url) return null;
    try {
      const response = await fetch(
        `/api/webex/recording/fetchContent?url=${encodeURIComponent(url)}`
      );
      if (!response.ok) return null;
      return await response.text();
    } catch {
      return null;
    }
  };

  const [suggestedNote, transcript, actionItems] = await Promise.allSettled([
    fetchText(urls.suggestedNoteUrl),
    fetchText(urls.transcriptURL),
    fetchText(urls.actionItemUrl),
  ]);

  let suggestedNoteJsonStr: string | null = null;
  if (suggestedNote.status === "fulfilled") {
    suggestedNoteJsonStr = (() => {
      try {
        const suggestedNoteJson = JSON.parse(suggestedNote.value);
        //console.log("Parsed suggested note JSON:", suggestedNoteJson);
        const suggestedNoteContentJson = JSON.parse(suggestedNoteJson?.content ?? "{}");
        const lines: string[] = [];
        lines.push(suggestedNoteContentJson?.description?.text ?? "");
        lines.push(
          suggestedNoteContentJson?.notes?.map((n: any) => `- ${n.text}`).join("\n") ?? ""
        );
        return lines.join("\n");
      } catch {
        return suggestedNote.value;
      }
    })();
  }

  let actionItemsJsonStr: string | null = null;
  if (actionItems.status === "fulfilled" && actionItems.value) {
    try {
      const actionItemsJson = JSON.parse(actionItems.value);
      const actionItemsContentJson = JSON.parse(actionItemsJson?.content ?? "{}");
      actionItemsJsonStr =
        actionItemsContentJson?.map((item: any) => `- ${item.content}`).join("\n") ?? null;
    } catch {
      actionItemsJsonStr = actionItems.value;
    }
  }

  let transcriptStr: string | null = null;
  if (transcript.status === "fulfilled" && transcript.value) {
    const cues = parseWebVtt(transcript.value);
    //console.log("Parsed transcript cues:", cues);
    transcriptStr = cues
      .sort((a, b) => a.id - b.id)
      .map((c) => `${c.speaker ? `**[${c.start.split(".")[0]}] ${c.speaker}:** ` : ""} ${c.text}`)
      .join("\n\n");
  }

  return {
    recordingName: urls.recordName,
    playUrl: urls.playUrl,
    suggestedNote: suggestedNoteJsonStr || null,
    transcript: transcriptStr || null,
    actionItems: actionItemsJsonStr || null,
  };
};

const buildPreview = async (title: string, link: string, password: string): Promise<string> => {
  if (!link) return "";
  //if (!link || !password) return "";

  const streamInfo: RecordingStreamInfo | undefined = await getRecordingStreamInfo(
    link,
    password
  ).catch((error) => {
    console.error("failed to get recording stream info", error);
    // toast.error(
    //   "Failed to fetch recording info. Please check the link and password.",
    // );
    return undefined;
  });

  const contents: RecordingContents | undefined = streamInfo
    ? await fetchRecordingContents(streamInfo).catch(() => undefined)
    : undefined;

  const lines: string[] = [];
  if (contents?.recordingName || title)
    lines.push(`Webex meeting recording: [${contents?.recordingName ?? title}](${link}) \n`);
  if (contents?.suggestedNote && contents.suggestedNote.trim() !== "")
    lines.push(`\n**Note:** \n\n${contents.suggestedNote}\n`);
  if (contents?.actionItems && contents.actionItems.trim() !== "")
    lines.push(`\n**Action Items:** \n\n${contents.actionItems}\n`);
  if (contents?.transcript && contents.transcript.trim() !== "")
    lines.push(`\n**Transcript:** \n\n${contents.transcript}\n`);
  return lines.join("\n");
};

export interface RecordingNoteProps {
  open: boolean;
  onClose: () => void;
  onReplace: (text: string) => void;
  onInsert: (text: string) => void;
}

const RecordingNote: FC<RecordingNoteProps> = ({ open, onClose, onReplace, onInsert }) => {
  const classes = useStyles();

  const [recordingLink, setRecordingLink] = useState("");
  const [password, setPassword] = useState("");
  const [selectedTitle, setSelectedTitle] = useState("");
  const [parsedRecordings, setParsedRecordings] = useState<ParsedRecording[]>([]);
  const [preview, setPreview] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Update preview whenever recording details change
  useEffect(() => {
    if (!selectedTitle && !recordingLink && !password) {
      setPreview("");
      return;
    }
    setPreviewLoading(true);
    buildPreview(selectedTitle, recordingLink, password)
      .then(setPreview)
      .finally(() => setPreviewLoading(false));
  }, [selectedTitle, recordingLink, password]);

  // Listen for paste events at the document level while the dialog is open
  useEffect(() => {
    if (!open) return;
    setRecordingLink("");
    setPassword("");
    setSelectedTitle("");
    setParsedRecordings([]);

    const handleDocumentPaste = (e: ClipboardEvent) => {
      // Skip if the target is an editable field (let the user paste normally there)
      const target = e.target as HTMLElement;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target.isContentEditable
      ) {
        return;
      }
      const text = e.clipboardData?.getData("text/plain") ?? "";
      if (text) extractRecordingsFromClipboard(text);
    };

    document.addEventListener("paste", handleDocumentPaste);
    return () => document.removeEventListener("paste", handleDocumentPaste);
  }, [open]);

  const extractRecordingsFromClipboard = (text: string) => {
    const recordings = parseClipboard(text);
    setParsedRecordings(recordings);
    if (recordings.length > 0) {
      handleSelectRecording(recordings[0]); // Auto-select the first recording if available
    }
  };

  const handlePasteClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      extractRecordingsFromClipboard(text);
    } catch {
      // Clipboard access denied or unavailable — silently ignore
    }
  };

  const handleSelectRecording = (rec: ParsedRecording) => {
    setRecordingLink(rec.link);
    setPassword(rec.password);
    setSelectedTitle(rec.title);
  };

  const handleReplace = () => {
    if (preview) onReplace(preview);
  };

  const handleInsert = () => {
    if (preview) onInsert(preview);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      {/* Title bar */}
      <div className={classes.titleContainer}>
        <Typography className={classes.title}>Add Webex meeting recording</Typography>
        <IconButton
          size="small"
          onClick={onClose}
        >
          <Close fontSize="small" />
        </IconButton>
      </div>

      <DialogContent className={classes.content}>
        {/* Clipboard parsed recordings */}
        <div className={classes.clipboardSection}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Typography className={classes.clipboardTitle}>
              Paste from clipboard (Ctrl+V)
            </Typography>
            <Tooltip
              title={
                <div>
                  Copy a Webex recording info to your clipboard in the following format to have it
                  automatically parsed here by clicking the button or pressing Ctrl+V anywhere on
                  this dialog:
                  <br />
                  <span style={{ fontStyle: "italic", color: "#535759" }}>
                    Webex meeting recording: [Title]
                    <br />
                    Recording link: [URL]
                    <br />
                    Password: [Password] (optional)
                    <br />
                  </span>
                  <b>Example:</b>
                  <br />
                  Webex meeting recording: Team Sync
                  <br />
                  Recording link: https://webex.com/recording/12345
                  <br />
                  Password: abcde
                </div>
              }
              placement="top"
              style={{
                // modify tooltip popper width to prevent it from being too narrow for the content
                maxWidth: 500,
              }}
            >
              <IconButton
                size="small"
                id="paste-from-clipboard"
                onClick={handlePasteClipboard}
              >
                <ContentPaste style={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </div>
          {parsedRecordings.length > 0 && (
            <div className={classes.chipRow}>
              {parsedRecordings.map((rec, idx) => (
                <Chip
                  key={idx}
                  label={rec.title}
                  className={classes.chip}
                  onClick={() => handleSelectRecording(rec)}
                  variant="outlined"
                  style={
                    rec.link === recordingLink && rec.password === password
                      ? {
                          backgroundColor: "#c7e5ea",
                          borderColor: "#007AA3",
                          fontWeight: 700,
                        }
                      : undefined
                  }
                />
              ))}
            </div>
          )}
        </div>

        {/* Recording link */}
        <div>
          <Typography className={classes.fieldLabel}>Recording link</Typography>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="https://"
            value={recordingLink}
            onChange={(e) => setRecordingLink(e.target.value)}
            inputProps={{ spellCheck: false }}
          />
        </div>

        {/* Password */}
        <div>
          <Typography className={classes.fieldLabel}>Password</Typography>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            inputProps={{ spellCheck: false }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                  >
                    {showPassword ? (
                      <VisibilityOff fontSize="small" />
                    ) : (
                      <Visibility fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </div>

        {/* Preview */}
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              marginBottom: 4,
            }}
          >
            <Typography
              className={classes.previewLabel}
              style={{ marginBottom: 0 }}
            >
              Preview
            </Typography>
            <Tooltip title="Reload preview">
              <span>
                <IconButton
                  size="small"
                  disabled={previewLoading || !recordingLink}
                  onClick={() => {
                    if (!selectedTitle && !recordingLink) return;
                    setPreviewLoading(true);
                    buildPreview(selectedTitle, recordingLink, password)
                      .then(setPreview)
                      .finally(() => setPreviewLoading(false));
                  }}
                >
                  <Refresh style={{ fontSize: 15 }} />
                </IconButton>
              </span>
            </Tooltip>
          </div>
          <div style={{ position: "relative" }}>
            <div
              style={{
                minHeight: 80,
                maxHeight: 280,
                overflowY: "auto",
                backgroundColor: "#F7F7F7",
                border: "1px solid rgba(0,0,0,0.23)",
                borderRadius: 4,
                padding: "8px 12px",
                fontSize: 13,
                color: preview ? "inherit" : "#9e9e9e",
              }}
            >
              {preview ? (
                <div
                  className="vditor-reset"
                  style={{ fontSize: 13 }}
                >
                  <ReactMarkdown>{preview}</ReactMarkdown>
                </div>
              ) : (
                !previewLoading && "Preview will appear here"
              )}
            </div>
            {previewLoading && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  backgroundColor: "rgba(247,247,247,0.75)",
                  borderRadius: 4,
                  pointerEvents: "none",
                }}
              >
                <CircularProgress
                  size={16}
                  thickness={5}
                />
                <Typography style={{ fontSize: 12, color: "#535759" }}>Loading preview…</Typography>
              </div>
            )}
          </div>
        </div>
      </DialogContent>

      <DialogActions className={classes.actions}>
        <MButton onClick={onClose}>Cancel</MButton>
        <MButton
          buttonType="blue-reverse"
          disabled={!preview}
          onClick={handleReplace}
        >
          Replace Note
        </MButton>
        <MButton
          buttonType="blue"
          disabled={!preview}
          onClick={handleInsert}
        >
          Insert Note
        </MButton>
      </DialogActions>
    </Dialog>
  );
};

export default RecordingNote;
